import { FC, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { formOptions } from '@tanstack/react-form';

import { useAppForm } from '@new/common/hooks/form';
import { Button } from '@new/common/ui/Button/Button';
import {
  useGetCourseCategoriesQuery,
  useReorderTranscriptCoursesMutation,
  useReplaceTranscriptCoursesMutation,
  useReviewTranscriptMutation,
  useSaveTranscriptProgressMutation,
  type TranscriptBreakdown,
} from '@teachur/http-client';

import { TranscriptStatusBadge } from '@new/features/transcripts/shared/components/StatusBadge';
import { reviewSchema, type ReviewFormValues } from './reviewSchema';
import {
  hasUnresolvedErrors,
  toCoursePayload,
  toEditableRow,
  type EditableCourseRow,
} from './courseRows';
import { useOfficialTranscriptValidation } from './useOfficialTranscriptValidation';
import { StudentTranscriptSection } from './StudentTranscriptSection';
import { OfficialTranscriptSection } from './OfficialTranscriptSection';
import { FacultyReviewSection } from './FacultyReviewSection';
import { unprocessedLegacyFiles } from './legacyDocuments';

interface TranscriptReviewPanelProps {
  transcript: TranscriptBreakdown;
  allTranscripts: TranscriptBreakdown[];
  onOpenTranscript?: (transcriptId: number) => void;
  onBack: () => void;
}

const sumCreditHours = (rows: EditableCourseRow[]): number =>
  Math.round(rows.reduce((acc, r) => acc + (Number(r.creditHours) || 0), 0));

export const TranscriptReviewPanel: FC<TranscriptReviewPanelProps> = ({
  transcript,
  allTranscripts,
  onOpenTranscript,
  onBack,
}) => {
  const review = useReviewTranscriptMutation();
  const saveProgress = useSaveTranscriptProgressMutation();
  const replaceCourses = useReplaceTranscriptCoursesMutation();
  const reorderCourses = useReorderTranscriptCoursesMutation();
  const categoriesQuery = useGetCourseCategoriesQuery();
  const categories = categoriesQuery.data ?? [];

  const [rows, setRows] = useState<EditableCourseRow[]>(() =>
    transcript.courses.map(toEditableRow)
  );
  const [hasManualCreditOverride, setHasManualCreditOverride] = useState(
    transcript.officialCredits != null
  );

  const validation = useOfficialTranscriptValidation(transcript.id, setRows);

  const onReorderCourses = (courseIds: number[]) => {
    reorderCourses.mutate({ id: transcript.id, body: { courseIds } });
  };

  const legacyFiles = unprocessedLegacyFiles(
    (transcript.files ?? []).filter((f) => f.kind === 'legacy'),
    allTranscripts
  );
  const documentFiles = (transcript.files ?? []).filter((f) => f.kind !== 'legacy');

  const initialApprovedCredits = transcript.courses.reduce(
    (sum, course) => sum + (course.creditHours ?? 0),
    0
  );

  const initialValues: ReviewFormValues = {
    decision: transcript.status === 'rejected' ? 'reject' : 'approve',
    officialCredits: transcript.officialCredits ?? initialApprovedCredits,
    officialFiles: [],
    reviewNote: transcript.reviewNote ?? '',
  };

  const onSubmit = async ({ value }: { value: ReviewFormValues }) => {
    if (hasUnresolvedErrors(rows)) {
      toast.error(
        'Please review and resolve each flagged course by choosing whether to accept it for credit before completing the review.'
      );
      return;
    }

    try {
      await replaceCourses.mutateAsync({
        id: transcript.id,
        body: { courses: toCoursePayload(rows) },
      });
    } catch {
      toast.error('Failed to save courses. Nothing was changed — please try again.');
      return;
    }

    const creditsToSubmit = hasManualCreditOverride ? value.officialCredits : sumCreditHours(rows);

    if (value.decision === 'approve' && (creditsToSubmit === null || creditsToSubmit <= 0)) {
      toast.error('At least 1 approved credit is required to approve a transcript');
      return;
    }

    try {
      await review.mutateAsync({
        id: transcript.id,
        body: {
          decision: value.decision,
          officialCredits: value.decision === 'reject' ? 0 : creditsToSubmit,
          officialFiles: value.decision === 'reject' ? null : validation.officialFiles,
          reviewNote: value.reviewNote.trim() || null,
        },
      });
    } catch {
      toast.error(
        'Courses were saved, but recording the decision failed. Press Save again to finish.'
      );
      return;
    }

    toast.success('Transcript saved');
    onBack();
  };

  const form = useAppForm({
    ...formOptions({ defaultValues: initialValues }),
    validators: {
      onChange: ({ value }: { value: ReviewFormValues }) => {
        const errors: Record<string, string> = {};
        const parsed = reviewSchema.safeParse(value);
        if (!parsed.success) {
          parsed.error.issues.forEach((issue) => {
            const field = issue.path[0];
            if (typeof field === 'string' && !errors[field]) errors[field] = issue.message;
          });
        }
        return { fields: errors };
      },
    },
    onSubmit,
  });

  useEffect(() => {
    if (!hasManualCreditOverride) {
      const calculatedCredits = sumCreditHours(rows);
      form.setFieldValue('officialCredits', calculatedCredits);
    }
  }, [rows, hasManualCreditOverride, form]);

  const onSaveProgress = async () => {
    try {
      await replaceCourses.mutateAsync({
        id: transcript.id,
        body: { courses: toCoursePayload(rows) },
      });
    } catch {
      toast.error('Failed to save courses. Nothing was changed — please try again.');
      return;
    }

    const creditsToSave = hasManualCreditOverride
      ? form.store.state.values.officialCredits
      : sumCreditHours(rows);

    try {
      await saveProgress.mutateAsync({
        id: transcript.id,
        body: {
          reviewNote: form.store.state.values.reviewNote.trim() || null,
          officialCredits: creditsToSave,
          officialFiles: validation.officialFiles,
        },
      });
    } catch {
      toast.error(
        'Courses were saved, but saving the note/credits/files failed. Please try again.'
      );
      return;
    }

    toast.success('Progress saved');
  };

  return (
    <div className="flex h-full flex-col bg-[#fafdff]">
      <div className="flex items-center justify-between gap-3 border-b border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) px-4 py-4 sm:px-6">
        <div className="shrink-0">
          <TranscriptStatusBadge status={transcript.status} />
        </div>
        <button
          type="button"
          onClick={onBack}
          className="shrink-0 cursor-pointer text-(--neutral-black-NB600) hover:text-(--neutral-black-NB900)"
          aria-label="Close review"
        >
          <X className="size-5" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-6 sm:px-6">
        <StudentTranscriptSection
          transcript={transcript}
          legacyFiles={legacyFiles}
          onOpenTranscript={onOpenTranscript}
        />

        <OfficialTranscriptSection
          form={form}
          documentFiles={documentFiles}
          rows={rows}
          categories={categories}
          onRowsChange={(updater) => setRows(updater)}
          onReorderCourses={onReorderCourses}
          pendingFiles={validation.files}
          onUploadOfficialFiles={validation.uploadOfficialFiles}
          onRemoveOfficialFile={validation.removeOfficialFile}
          onRetryValidation={validation.retryValidation}
        />

        <FacultyReviewSection
          form={form}
          requestedCredits={transcript.unofficialCredits}
          rows={rows}
          sumCreditHours={sumCreditHours}
          hasManualCreditOverride={hasManualCreditOverride}
          onManualCreditOverride={() => setHasManualCreditOverride(true)}
        />
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) px-4 py-4 sm:px-6">
        <Button variant="neutral" size="sm" onClick={onBack} text="Cancel" />
        <form.Subscribe selector={(s) => [s.isSubmitting] as const}>
          {([isSubmitting]) => {
            const savingProgress = saveProgress.isPending || replaceCourses.isPending;
            const finishing = isSubmitting || review.isPending || replaceCourses.isPending;
            const filesBusy =
              validation.isFileUploading || validation.validationPhase === 'validating';

            return (
              <>
                <Button
                  variant="neutral"
                  size="sm"
                  disabled={savingProgress || finishing || filesBusy}
                  onClick={onSaveProgress}
                  text={savingProgress ? 'Saving…' : 'Save progress'}
                />
                <Button
                  variant="primary"
                  size="sm"
                  disabled={finishing || savingProgress || filesBusy}
                  onClick={() => form.handleSubmit()}
                  text={finishing ? 'Finishing…' : 'Finish'}
                />
              </>
            );
          }}
        </form.Subscribe>
      </div>
    </div>
  );
};
